import React from 'react';
import { Box, IconButton, Typography, alpha, useTheme } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import { borderRadius, componentSizes, getSemanticSurfaceTokens } from '../../../theme/theme';
import HelpTooltipTrigger from '../common/HelpTooltipTrigger';
import type { HelpValue } from '../common/helpModel';

export interface WorkspaceMobileHeaderProps {
  title: React.ReactNode;
  onMenuToggle: () => void;
  menuButtonRef?: React.RefObject<HTMLButtonElement | null>;
  help?: HelpValue;
  mobileMenuOpen?: boolean;
  dataTestId?: string;
  sx?: SxProps<Theme>;
}

/**
 * Compact mobile header used inside workspace content areas.
 *
 * Provides a consistent menu toggle, title, and optional help tooltip so
 * individual features do not re-implement the same header layout.
 */
export const WorkspaceMobileHeader: React.FC<WorkspaceMobileHeaderProps> = ({
  title,
  onMenuToggle,
  menuButtonRef,
  help,
  mobileMenuOpen = false,
  dataTestId,
  sx,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);

  return (
    <Box
      data-testid={dataTestId}
      sx={[
        {
          px: { xs: 0.5, sm: 1 },
          py: 0.25,
          minHeight: componentSizes.toolbarCompact,
          borderBottom: `1px solid ${colors.border.default}`,
          backgroundColor: colors.background.elevated,
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <IconButton
        ref={menuButtonRef}
        onClick={onMenuToggle}
        size="small"
        aria-label="Open navigation menu"
        aria-expanded={mobileMenuOpen ? 'true' : 'false'}
        data-testid="workspace-mobile-header-menu"
        sx={{
          width: 28,
          height: 28,
          p: 0.25,
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: `${borderRadius.md}px`,
          color: colors.text.secondary,
          backgroundColor: colors.background.panel,
          '&:hover': {
            backgroundColor: colors.background.hover,
          },
        }}
      >
        <MenuIcon sx={{ fontSize: 17 }} />
      </IconButton>
      <Typography
        variant="subtitle2"
        sx={{
          minWidth: 0,
          flex: '0 1 auto',
          fontWeight: 600,
          fontSize: '0.75rem',
          color: colors.text.primary,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: 1,
        }}
      >
        {title}
      </Typography>
      {help ? (
        <HelpTooltipTrigger
          label={typeof title === 'string' ? title : 'Current section'}
          help={help}
          placement="bottom-start"
          iconSize={16}
          sx={{
            width: 28,
            height: 28,
            minWidth: 28,
            minHeight: 28,
            borderRadius: `${borderRadius.md}px`,
            flexShrink: 0,
          }}
        />
      ) : null}
    </Box>
  );
};

export default WorkspaceMobileHeader;
